import {
  Box,
  Button,
  Container,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { usePageViewTracking } from '@diamond/shared/utils';
import { TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { SEONext } from '@diamond/sol-buyer/seo-next';
import {
  buildNooRegistrationStatusUrlWithSection,
  NOO_STATUS_DESKTOP_MEDIA_QUERY,
  useNooRegistrationStatus,
} from '@diamond/sol-buyer/utils';
import { useRouter } from 'next/router';
import { type ReactNode, useEffect } from 'react';

import {
  MobileBusinessDetailContent,
  MobileOutletDetailContent,
} from './NooRegistrationStatusDetailFields';
import { NooRegistrationStatusMobileSubHeader } from './NooRegistrationStatusMobileSubHeader';
import { SupportSection } from './NooRegistrationStatusSharedUi';

function useDesktopSectionRedirect(sectionId: string | null) {
  const router = useRouter();

  useEffect(() => {
    if (!sectionId || typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia(NOO_STATUS_DESKTOP_MEDIA_QUERY);

    const redirectIfDesktop = () => {
      if (media.matches) {
        void router.replace(buildNooRegistrationStatusUrlWithSection(sectionId));
      }
    };

    redirectIfDesktop();
    media.addEventListener('change', redirectIfDesktop);
    return () => media.removeEventListener('change', redirectIfDesktop);
  }, [router, sectionId]);
}

function MobileSectionDetailShell({
  title,
  seoTitle,
  isLoading,
  error,
  registrationToken,
  onRetry,
  children,
}: {
  title: string;
  seoTitle: string;
  isLoading: boolean;
  error: string | null;
  registrationToken: string;
  onRetry: () => void;
  children: ReactNode;
}) {
  return (
    <>
      <SEONext title={seoTitle} />
      <Box minH="100vh" bg="#FFFFFF" display={{ base: 'block', lg: 'none' }}>
        <NooRegistrationStatusMobileSubHeader title={title} />

        <Container maxW="390px" px={4} pt={6} pb={8}>
          {isLoading ? (
            <Flex justify="center" py={12} data-testid="noo-buyer-status-loading">
              <Spinner size="lg" color="#2563EB" />
            </Flex>
          ) : null}

          {error ? (
            <Box
              border="1px solid"
              borderColor="#FECACA"
              bg="#FEF2F2"
              borderRadius="xl"
              p={4}
              data-testid="noo-buyer-status-error"
            >
              <Text color="#DC2626" fontSize="sm">
                {error}
              </Text>
              {registrationToken ? (
                <Button
                  mt={4}
                  size="sm"
                  variant="outline"
                  onClick={() => void onRetry()}
                >
                  Coba Lagi
                </Button>
              ) : null}
            </Box>
          ) : null}

          {!isLoading && !error ? children : null}

          {!isLoading && !error ? (
            <Box mt={8}>
              <SupportSection />
            </Box>
          ) : null}
        </Container>
      </Box>
    </>
  );
}

export function NooRegistrationStatusMobileBusinessPage() {
  usePageViewTracking({ pageName: TRACKING_PAGES.NOO_REGISTRATION_STATUS });

  const {
    progress,
    isLoading,
    error,
    registrationToken,
    loadStatus,
    verificationSections,
  } = useNooRegistrationStatus();

  const businessSection = verificationSections.find(
    (section) => section.id === 'business'
  );

  useDesktopSectionRedirect('business');

  return (
    <MobileSectionDetailShell
      title={businessSection?.title ?? 'Data Badan Usaha'}
      seoTitle={TRACKING_PAGES.NOO_REGISTRATION_STATUS}
      isLoading={isLoading}
      error={error}
      registrationToken={registrationToken}
      onRetry={loadStatus}
    >
      {progress?.business && businessSection ? (
        <Box data-testid="noo-buyer-status-mobile-business-detail">
          <MobileBusinessDetailContent
            business={progress.business}
            verificationState={businessSection.state}
          />
        </Box>
      ) : (
        <Text fontSize="sm" color="#637381">
          Data badan usaha belum tersedia.
        </Text>
      )}
    </MobileSectionDetailShell>
  );
}

export function NooRegistrationStatusMobileOutletPage() {
  usePageViewTracking({ pageName: TRACKING_PAGES.NOO_REGISTRATION_STATUS });

  const router = useRouter();
  const outletIdQuery = router.query['outletId'];
  const outletId = typeof outletIdQuery === 'string' ? outletIdQuery : '';

  const {
    progress,
    isLoading,
    error,
    registrationToken,
    loadStatus,
    verificationSections,
    displayInfo,
  } = useNooRegistrationStatus();

  const outletSection = verificationSections.find(
    (section) => section.id === outletId
  );
  const outlet =
    progress?.outlets.find((item) => item.id === outletId) ?? undefined;

  useDesktopSectionRedirect(outletId || null);

  return (
    <MobileSectionDetailShell
      title="Detail Outlet"
      seoTitle={TRACKING_PAGES.NOO_REGISTRATION_STATUS}
      isLoading={isLoading || !router.isReady}
      error={error}
      registrationToken={registrationToken}
      onRetry={loadStatus}
    >
      {outlet && outletSection ? (
        <Box data-testid="noo-buyer-status-mobile-outlet-detail">
          <MobileOutletDetailContent
            outlet={outlet}
            picName={displayInfo?.picName ?? null}
            verificationState={outletSection.state}
          />
        </Box>
      ) : (
        <Text fontSize="sm" color="#637381">
          Data outlet tidak ditemukan.
        </Text>
      )}
    </MobileSectionDetailShell>
  );
}
